import React, { useState, useEffect } from 'react'; 
import { getProcurementSettings } from '../../models/procurementModel';

function MultiFileInput({ 
  files = [], 
  onChange, 
  accept = null, // Will be overridden by settings
  maxSize = null, // Will be overridden by settings
  multiple = true,
  className = ""
}) {
  const [dragActive, setDragActive] = useState(false);
  const [fileSettings, setFileSettings] = useState({
    acceptTypes: ".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif",
    maxFileSize: 10 * 1024 * 1024, // 10MB default
    enforceLimit: false
  });

  // Fetch procurement settings on component mount
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const settings = await getProcurementSettings();
        
        // Convert comma-separated extensions to accept format (.pdf,.jpg etc)
        let acceptTypes = "";
        if (settings.site_allowed_ext) {
          acceptTypes = settings.site_allowed_ext
            .split(',')
            .map(ext => ext.trim())
            .filter(ext => ext) // Filter out empty strings
            .map(ext => ext.startsWith('.') ? ext : `.${ext}`)
            .join(',');
        }

        // Convert MB to bytes
        const sizeLimit = settings.site_upload_limit ? 
          parseInt(settings.site_upload_limit) * 1024 * 1024 : 
          10 * 1024 * 1024; // Default 10MB
          
        setFileSettings({
          acceptTypes: acceptTypes || fileSettings.acceptTypes,
          maxFileSize: sizeLimit,
          enforceLimit: settings.site_enforce_limit === "1" || settings.site_enforce_limit === 1
        });
      } catch (error) {
        console.error("Error loading file upload settings:", error);
      }
    };
    
    fetchSettings();
  }, []);

  const handleFiles = (newFiles, e) => {
    // Prevent form submission
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    const fileArray = Array.from(newFiles);
    
    // Validate file sizes if enforce limit is enabled
    const validFiles = fileArray.filter(file => {
      if (fileSettings.enforceLimit && file.size > fileSettings.maxFileSize) {
        alert(`File "${file.name}" is too large. Maximum size is ${Math.round(fileSettings.maxFileSize / 1024 / 1024)}MB.`);
        return false;
      }
      return true;
    });

    if (multiple) {
      onChange([...files, ...validFiles]);
    } else {
      onChange(validFiles.length > 0 ? [validFiles[0]] : []);
    }
  };

  const handleFileInput = (e) => {
    // Stop any potential form submission
    e.preventDefault();
    e.stopPropagation();
    
    // Create a local copy of the files before the event is lost
    const selectedFiles = Array.from(e.target.files || []);
    
    if (selectedFiles.length > 0) {
      // Use setTimeout to break the event chain
      setTimeout(() => {
        handleFiles(selectedFiles, null);
      }, 0);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    // Create a local copy of the files before the event is lost
    const droppedFiles = Array.from(e.dataTransfer.files || []);
    
    if (droppedFiles.length > 0) {
      // Use setTimeout to break the event chain
      setTimeout(() => {
        handleFiles(droppedFiles, null);
      }, 0);
    }
  };

  const removeFile = (index, e) => {
    // Prevent the event from bubbling up and potentially submitting a form
    if (e) e.preventDefault();
    
    const newFiles = files.filter((_, i) => i !== index);
    onChange(newFiles);
  };

  // Handle paste event for images
  const handlePaste = (e) => {
    const clipboardData = e.clipboardData || window.clipboardData;
    if (!clipboardData) return;

    const items = clipboardData.items;
    const pastedFiles = [];

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      
      // Check if the item is a file (image)
      if (item.kind === 'file') {
        const file = item.getAsFile();
        if (file) {
          // If accept prop is set and it's for images, check if the file is an image
          if (accept && accept.includes('image')) {
            if (file.type.startsWith('image/')) {
              pastedFiles.push(file);
            }
          } else {
            pastedFiles.push(file);
          }
        }
      }
    }

    if (pastedFiles.length > 0) {
      e.preventDefault();
      e.stopPropagation();
      handleFiles(pastedFiles, null);
    }
  };

  // Add paste event listener
  useEffect(() => {
    document.addEventListener('paste', handlePaste);
    return () => {
      document.removeEventListener('paste', handlePaste);
    };
  }, [files, accept, fileSettings]); // Re-add listener when these change

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className={className} onClick={(e) => e.stopPropagation()}>
      {/* Upload Area */}
      <div
        className={`relative border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
          dragActive 
            ? 'border-blue-400 bg-blue-50' 
            : 'border-gray-300 bg-gray-50 hover:bg-gray-100'
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={(e) => e.stopPropagation()}
      >
        <label className="flex flex-col items-center justify-center w-full h-32 cursor-pointer" onClick={(e) => e.stopPropagation()}>
          <div className="flex flex-col items-center justify-center pt-5 pb-6">
            <svg 
              className={`w-8 h-8 mb-4 ${dragActive ? 'text-blue-500' : 'text-gray-500'}`} 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth="2" 
                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
              />
            </svg>
            <p className={`mb-2 text-sm ${dragActive ? 'text-blue-600' : 'text-gray-500'}`}>
              <span className="font-semibold">Click to upload</span> or drag and drop
            </p>
            <p className={`text-xs ${dragActive ? 'text-blue-500' : 'text-gray-500'}`}>
              {multiple ? 'Multiple files supported' : 'Single file only'}
              {fileSettings.enforceLimit && ` • Max ${Math.round(fileSettings.maxFileSize / 1024 / 1024)}MB each`}
            </p>
          </div>
          <input
            type="file"
            onChange={handleFileInput}
            className="hidden"
            accept={accept || fileSettings.acceptTypes}
            multiple={multiple}
            onClick={(e) => {
              // Prevent the click event from bubbling up
              e.stopPropagation();
            }}
          />
        </label>
      </div>

      {/* File List */}
      {files.length > 0 && (
        <div className="mt-4 space-y-2">
          <h4 className="text-sm font-medium text-gray-700">
            Selected Files ({files.length})
          </h4>
          <div className="max-h-40 overflow-y-auto space-y-2">
            {files.map((file, index) => (
              <div 
                key={index} 
                className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg"
              >
                <div className="flex items-center space-x-3 min-w-0 flex-1">
                  <div className="flex-shrink-0">
                    {file.type?.startsWith('image/') ? (
                      <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                      </svg>
                    ) : file.type?.includes('pdf') ? (
                      <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {file.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {formatFileSize(file.size)}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={(e) => removeFile(index, e)}
                  className="flex-shrink-0 ml-3 p-1 text-red-600 hover:text-red-800 transition-colors"
                  title="Remove file"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default MultiFileInput;
